/**
 * Admin Operations · All Orders hub (Phase A)
 * ─────────────────────────────────────────────────────────────
 * A single admin destination that lists every supported order source:
 *   • Sticker orders (missing-sticker, manual entry, converted quotes)
 *   • Certificate orders
 * Search text (customer name / email / phone) stays in component state
 * and is POSTed to the backend — never persisted to the URL or router
 * history. Non-sensitive filters DO round-trip through URL parameters.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import {
  ArrowLeft, Filter, Loader2, RefreshCw, Search, X,
  ChevronLeft, ChevronRight, AlertTriangle, MapPin, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { readUrlFilters, toUrlParams, QUICK_VIEWS, applyQuickView } from "./adminAllOrdersHelpers";

// ─── Vocabulary lists (mirror backend) ────────────────────────────

const PRESETS = [
  { value: "today",       label: "Today" },
  { value: "yesterday",   label: "Yesterday" },
  { value: "last_7",      label: "Last 7 Days" },
  { value: "last_30",     label: "Last 30 Days" },
  { value: "this_month",  label: "This Month" },
  { value: "last_month",  label: "Last Month" },
  { value: "this_year",   label: "This Year" },
  { value: "custom",      label: "Custom Range" },
  { value: "all_time",    label: "All Time" },
];

const DATE_BASES = [
  { value: "created",    label: "Created" },
  { value: "payment",    label: "Payment" },
  { value: "completion", label: "Completion" },
];

const STATUSES = [
  "awaiting_payment","payment_submitted","payment_needs_review",
  "payment_verified","preparing","ready",
  "shipped","completed","cancelled",
  "refunded","partially_refunded",
];

const STATUS_LABELS = {
  awaiting_payment: "Awaiting Payment",
  payment_submitted: "Payment Submitted",
  payment_needs_review: "Payment Needs Review",
  payment_verified: "Payment Verified",
  preparing: "Preparing",
  ready: "Ready",
  shipped: "Shipped",
  completed: "Completed",
  cancelled: "Cancelled",
  refunded: "Refunded",
  partially_refunded: "Partially Refunded",
};

const PAYMENT_METHODS = ["zelle","cash","cashapp","card","shopify_checkout","ebay","other","unknown"];
const PAYMENT_METHOD_LABELS = {
  zelle: "Zelle", cash: "Cash", cashapp: "Cash App",
  card: "Credit/Debit Card", shopify_checkout: "Shopify Checkout",
  ebay: "eBay",
  other: "Other", unknown: "Unknown",
};

const FULFILLMENT_GROUPS = [
  { value: "pickup",  label: "Local Pickup" },
  { value: "ship",    label: "Shipping" },
  { value: "unknown", label: "Delivery Review Needed" },
];

const SOURCES = ["Instagram","Facebook","TikTok","WhatsApp","Event","Walk-in","Phone","Referral","Website","Unknown"];

const SOURCE_TYPES = [
  { value: "sticker",             label: "Sticker order" },
  { value: "sticker_from_quote",  label: "Converted quote" },
  { value: "certificate",         label: "Certificate order" },
];

const SORTS = [
  { value: "newest",              label: "Newest first" },
  { value: "oldest",              label: "Oldest first" },
  { value: "recently_paid",       label: "Recently paid" },
  { value: "recently_completed",  label: "Recently completed" },
  { value: "highest_total",       label: "Highest total" },
  { value: "lowest_total",        label: "Lowest total" },
  { value: "oldest_waiting",      label: "Oldest waiting action" },
];

// ─── Helpers ──────────────────────────────────────────────────────

const money = (cents) => {
  if (cents === null || cents === undefined) return "—";
  const dollars = (Number(cents) / 100).toFixed(2);
  return `$${dollars}`;
};

const dt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
      timeZone: "America/New_York",
    });
  } catch { return iso; }
};

const age = (seconds) => {
  if (!seconds) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

// URL-safe filter serialisation lives in `./adminAllOrdersHelpers` so
// it can be unit-tested without React / jsdom overhead.
function writeUrlFilters(setParams, filters) {
  setParams(toUrlParams(filters), { replace: true });
}

// ─── Badge components ────────────────────────────────────────────

function FulfillmentBadge({ group }) {
  const g = String(group || "").toLowerCase();
  const map = {
    pickup:  { label: "PICKUP",   Icon: MapPin,        tone: "bg-emerald-100 text-emerald-900 border-emerald-700" },
    ship:    { label: "SHIPPING", Icon: Truck,         tone: "bg-blue-100 text-blue-900 border-blue-700" },
    unknown: { label: "DELIVERY REVIEW NEEDED", Icon: AlertTriangle, tone: "bg-red-100 text-red-900 border-red-700" },
  };
  const cfg = map[g] || map.unknown;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border-2 font-bold uppercase tracking-widest text-[9px] px-1.5 py-0.5 ${cfg.tone}`}
          data-testid={`all-orders-fulfillment-${g}`}>
      <cfg.Icon size={10} /> {cfg.label}
    </span>
  );
}

function StatusPill({ status }) {
  const label = STATUS_LABELS[status] || status;
  const tone = ({
    completed: "bg-gray-200 text-gray-800",
    cancelled: "bg-red-100 text-red-900",
    ready: "bg-emerald-100 text-emerald-900",
    shipped: "bg-blue-100 text-blue-900",
    preparing: "bg-yellow-100 text-yellow-900",
    payment_verified: "bg-yellow-50 text-yellow-900",
    payment_submitted: "bg-orange-100 text-orange-900",
    awaiting_payment: "bg-orange-50 text-orange-900",
  })[status] || "bg-gray-100 text-gray-700";
  return <span className={`inline-block text-[10px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 ${tone}`}>{label}</span>;
}

// ─── Filter chip UI (mobile-first) ───────────────────────────────

function MultiChip({ label, value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    const next = value.includes(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange(next);
  };
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        data-testid={testid}
        className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm font-bold transition ${
          value.length ? "bg-[var(--ink)] text-[var(--paper)]" : "bg-[var(--paper)] text-[var(--ink)]"
        }`}
      >
        {label}{value.length ? ` · ${value.length}` : ""}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-2 left-0 w-64 max-h-72 overflow-auto bg-[var(--paper)] border-2 border-[var(--ink)] rounded-xl shadow-[4px_4px_0_var(--ink)] p-2">
            {options.map((o) => {
              const v = typeof o === "string" ? o : o.value;
              const lbl = typeof o === "string" ? (STATUS_LABELS[o] || PAYMENT_METHOD_LABELS[o] || o) : o.label;
              return (
                <label key={v} className="flex items-center gap-2 px-2 py-1.5 hover:bg-[var(--paper-alt,#f5f5f5)] cursor-pointer rounded">
                  <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)} />
                  <span className="text-sm">{lbl}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────

export default function AdminAllOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => readUrlFilters(searchParams));
  const [searchText, setSearchText] = useState(""); // PII — component state only
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Sync URL <-> filters (non-sensitive only).
  useEffect(() => {
    writeUrlFilters(setSearchParams, filters);
  }, [filters, setSearchParams]);

  // Debounce search text (300ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        preset: filters.preset,
        date_basis: filters.date_basis,
        date_from: filters.date_from,
        date_to: filters.date_to,
        status: filters.status,
        payment_method: filters.payment_method,
        fulfillment_group: filters.fulfillment_group,
        source: filters.source,
        source_type: filters.source_type,
        sort: filters.sort,
        page: filters.page,
        page_size: pageSize,
        q: debouncedSearch || null,
        view: filters.view || null,
      };
      const r = await api.post("/admin/all-orders", body);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load orders.");
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const clearAll = () => {
    setFilters({
      preset: "last_30", date_basis: "created", date_from: null, date_to: null,
      status: [], payment_method: [], fulfillment_group: [], source: [], source_type: [],
      sort: "newest", page: 1,
    });
    setSearchText("");
  };

  const set = (patch) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));

  const totalPages = data?.total_pages || 1;
  const activeCount = (
    filters.status.length + filters.payment_method.length +
    filters.fulfillment_group.length + filters.source.length +
    filters.source_type.length + (filters.preset !== "last_30" ? 1 : 0) +
    (filters.date_basis !== "created" ? 1 : 0) + (debouncedSearch ? 1 : 0)
  );

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <SEO title="All Orders · Admin" description="Admin — all orders" />
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="flex items-center gap-2">
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm underline" data-testid="all-orders-back">
              <ArrowLeft size={14} /> Admin
            </Link>
            <h1 className="text-2xl sm:text-3xl font-display leading-none">All Orders</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="p-2 border-2 border-[var(--ink)] rounded-lg hover:-translate-y-0.5 transition" data-testid="all-orders-refresh" title="Refresh">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Sources banner */}
        {data && (
          <div className="mb-3 text-xs opacity-70" data-testid="all-orders-sources-banner">
            Sources included: <b>{(data.included_sources || []).map((s) => ({
              sticker_orders: "Sticker Orders",
              certificate_orders: "Certificate Orders",
            })[s] || s).join(", ")}</b>
            {(data.excluded_sources || []).length > 0 && (
              <span> · Excluded: {(data.excluded_sources || []).map((e) => `${e.source} (${e.reason})`).join(", ")}</span>
            )}
          </div>
        )}

        {/* Quick-view chips row (Phase B) */}
        <div className="flex flex-wrap gap-1.5 mb-3 text-xs" data-testid="all-orders-quick-views">
          {QUICK_VIEWS.map((v) => {
            const active = (
              (v.filters.preset || "last_30") === filters.preset &&
              (v.filters.date_basis || "created") === filters.date_basis &&
              JSON.stringify(v.filters.status || []) === JSON.stringify(filters.status) &&
              JSON.stringify(v.filters.fulfillment_group || []) === JSON.stringify(filters.fulfillment_group)
            );
            return (
              <button
                key={v.key}
                onClick={() => { setFilters(applyQuickView(filters, v)); setSearchText(""); }}
                data-testid={`all-orders-qv-${v.key}`}
                className={`px-2 py-1 rounded-full border-2 font-bold ${active ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "bg-[var(--paper)] text-[var(--ink)] border-[var(--ink)]/50"}`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        {/* Search bar */}
        <div className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search public order #, customer name, email, or phone…"
              className="w-full pl-9 pr-9 py-2 border-2 border-[var(--ink)] rounded-lg text-sm bg-[var(--paper)]"
              data-testid="all-orders-search-input"
              autoComplete="off"
              spellCheck={false}
            />
            {searchText && (
              <button onClick={() => setSearchText("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1" data-testid="all-orders-search-clear">
                <X size={14} />
              </button>
            )}
          </div>
          <button onClick={() => setShowFilters((s) => !s)} className="px-3 py-2 border-2 border-[var(--ink)] rounded-lg text-sm inline-flex items-center gap-1" data-testid="all-orders-filters-toggle">
            <Filter size={14} /> Filters{activeCount ? ` · ${activeCount}` : ""}
          </button>
        </div>

        {/* Filter chips */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              value={filters.preset}
              onChange={(e) => set({ preset: e.target.value })}
              className="px-3 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm font-bold bg-[var(--paper)]"
              data-testid="all-orders-preset">
              {PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <select
              value={filters.date_basis}
              onChange={(e) => set({ date_basis: e.target.value })}
              className="px-3 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm font-bold bg-[var(--paper)]"
              data-testid="all-orders-date-basis">
              {DATE_BASES.map((b) => <option key={b.value} value={b.value}>{b.label} date</option>)}
            </select>
            {filters.preset === "custom" && (
              <>
                <input type="date" value={filters.date_from || ""} onChange={(e) => set({ date_from: e.target.value })} className="px-2 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm" data-testid="all-orders-date-from" />
                <input type="date" value={filters.date_to || ""} onChange={(e) => set({ date_to: e.target.value })} className="px-2 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm" data-testid="all-orders-date-to" />
              </>
            )}
            <MultiChip testid="all-orders-status-chip" label="Status" value={filters.status} options={STATUSES} onChange={(v) => set({ status: v })} />
            <MultiChip testid="all-orders-payment-chip" label="Payment" value={filters.payment_method} options={PAYMENT_METHODS} onChange={(v) => set({ payment_method: v })} />
            <MultiChip testid="all-orders-fulfillment-chip" label="Fulfillment" value={filters.fulfillment_group} options={FULFILLMENT_GROUPS} onChange={(v) => set({ fulfillment_group: v })} />
            <MultiChip testid="all-orders-source-chip" label="Source" value={filters.source} options={SOURCES} onChange={(v) => set({ source: v })} />
            <MultiChip testid="all-orders-source-type-chip" label="Order Type" value={filters.source_type} options={SOURCE_TYPES} onChange={(v) => set({ source_type: v })} />
            <select
              value={filters.sort}
              onChange={(e) => set({ sort: e.target.value })}
              className="px-3 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm font-bold bg-[var(--paper)]"
              data-testid="all-orders-sort">
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            {activeCount > 0 && (
              <button onClick={clearAll} className="px-3 py-1.5 border-2 border-red-700 text-red-700 rounded-full text-sm font-bold" data-testid="all-orders-clear">
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Result meta */}
        <div className="flex items-center justify-between text-xs mb-2" data-testid="all-orders-result-meta">
          <div>
            {loading ? (
              <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> loading…</span>
            ) : data ? (
              <span><b data-testid="all-orders-total">{data.total.toLocaleString()}</b> matching order{data.total === 1 ? "" : "s"} · page {data.page} of {totalPages}</span>
            ) : null}
          </div>
          {data && Object.keys(data.data_quality_summary || {}).length > 0 && (
            <div className="text-[10px] opacity-70 flex items-center gap-1" data-testid="all-orders-dq-summary">
              <AlertTriangle size={11} />
              {Object.entries(data.data_quality_summary).map(([k, v]) => `${k}=${v}`).join(" · ")}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="border-2 border-[var(--ink)] rounded-2xl overflow-hidden bg-[var(--paper)]">
          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm min-w-[720px]" data-testid="all-orders-table">
              <thead className="bg-[var(--ink)] text-[var(--paper)]">
                <tr>
                  <Th>Order</Th><Th>Type</Th><Th>Created</Th><Th>Customer</Th>
                  <Th className="text-right">Items</Th>
                  <Th className="text-right">Total</Th>
                  <Th>Payment</Th><Th>Status</Th><Th>Fulfillment</Th><Th>Age</Th><Th />
                </tr>
              </thead>
              <tbody>
                {(data?.orders || []).map((o) => (
                  <tr key={`${o.source_type}-${o.public_order_reference}`}
                      className="border-t border-[var(--ink)]/10 hover:bg-[var(--paper-alt,#f8f8f8)]"
                      data-testid={`all-orders-row-${o.public_order_reference}`}>
                    <Td className="font-mono text-xs whitespace-nowrap">{o.public_order_reference}</Td>
                    <Td>
                      <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-gray-100" data-testid={`all-orders-source-${o.source_type}`}>
                        {({ sticker: "sticker", sticker_from_quote: "quote → sticker", certificate: "certificate" })[o.source_type] || o.source_type}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">{dt(o.created_at)}</Td>
                    <Td className="truncate max-w-[140px]" title={o.customer_display_name}>{o.customer_display_name || "Unknown"}</Td>
                    <Td className="text-right">{o.item_count ?? "—"}</Td>
                    <Td className="text-right whitespace-nowrap">{money(o.customer_total_cents)}</Td>
                    <Td>
                      <div className="text-[10px] font-bold uppercase tracking-widest">
                        {o.payment_method === "unknown" ? "Unknown" : (PAYMENT_METHOD_LABELS[o.payment_method] || o.payment_method)}
                      </div>
                      <div className="text-[9px] opacity-70">{STATUS_LABELS[o.payment_status] || o.payment_status}</div>
                    </Td>
                    <Td><StatusPill status={o.operational_status} /></Td>
                    <Td><FulfillmentBadge group={o.fulfillment_group} /></Td>
                    <Td className="whitespace-nowrap text-[10px] opacity-70">{age(o.age_in_current_status_s)}</Td>
                    <Td className="whitespace-nowrap">
                      <button
                        onClick={() => {
                          if (o.source_type === "certificate") navigate("/admin/certificate-orders");
                          else navigate(`/admin/fulfillment/${o.public_order_reference}`);
                        }}
                        className="px-2 py-1 border-2 border-[var(--ink)] rounded font-bold text-[10px] uppercase tracking-widest"
                        data-testid={`all-orders-open-${o.public_order_reference}`}>
                        Open
                      </button>
                    </Td>
                  </tr>
                ))}
                {!loading && (data?.orders || []).length === 0 && (
                  <tr><td colSpan={11} className="text-center py-10 opacity-70">No orders match the current filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {data && totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-sm" data-testid="all-orders-pagination">
            <button
              disabled={filters.page <= 1}
              onClick={() => set({ page: Math.max(1, filters.page - 1) })}
              className="px-3 py-1.5 border-2 border-[var(--ink)] rounded-lg disabled:opacity-30 inline-flex items-center gap-1"
              data-testid="all-orders-prev-page">
              <ChevronLeft size={14} /> Previous
            </button>
            <div>Page {filters.page} of {totalPages}</div>
            <button
              disabled={filters.page >= totalPages}
              onClick={() => set({ page: Math.min(totalPages, filters.page + 1) })}
              className="px-3 py-1.5 border-2 border-[var(--ink)] rounded-lg disabled:opacity-30 inline-flex items-center gap-1"
              data-testid="all-orders-next-page">
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left text-[10px] uppercase tracking-widest font-bold px-3 py-2 ${className}`}>{children}</th>;
}
function Td({ children, className = "", ...props }) {
  return <td className={`px-3 py-2 align-middle ${className}`} {...props}>{children}</td>;
}
